<template>
  <div class="role-list-page">
    <a-row class="toolbar" :gutter="16">
      <a-col :span="8">
        <a-input-search
          v-model="queryName"
          allow-clear
          placeholder="角色名（模糊）"
          enter-button="查询"
          @search="handleSearch"
        />
      </a-col>
      <a-col :span="16" style="text-align: right">
        <CommonPermissions :p="$permissions.ROLE.CREATE">
          <a-button type="primary" @click="openDrawer()">新建角色</a-button>
        </CommonPermissions>
      </a-col>
    </a-row>

    <a-table
      :columns="columns"
      :data-source="roleRows"
      :loading="loading"
      :pagination="pagination"
      row-key="id"
      @change="handleTableChange"
    >
      <template slot="permissions" slot-scope="text, record">
        <span v-if="permissionTagList(record).length === 0" class="perm-empty">未配置</span>
        <template v-else>
          <a-tag v-for="(label, i) in permissionTagList(record).slice(0, 8)" :key="i" style="margin-bottom: 4px">
            {{ label }}
          </a-tag>
          <span v-if="permissionTagList(record).length > 8" class="perm-more">
            +{{ permissionTagList(record).length - 8 }}
          </span>
        </template>
      </template>
      <template slot="action" slot-scope="text, record">
        <CommonPermissions :p="$permissions.ROLE.UPDATE">
          <a @click="openDrawer(record.id)">编辑</a>
        </CommonPermissions>
        <CommonPermissions :p="$permissions.ROLE.DELETE">
          <a-divider type="vertical" />
          <a-popconfirm title="确认删除该角色？" ok-text="是" cancel-text="否" @confirm="() => removeRole(record.id)">
            <a style="color: #f5222d">删除</a>
          </a-popconfirm>
        </CommonPermissions>
      </template>
    </a-table>

    <a-drawer
      :title="drawerTitle"
      width="520"
      placement="right"
      :visible="drawerVisible"
      :closable="true"
      destroy-on-close
      @close="closeDrawer"
    >
      <a-spin :spinning="drawerLoading">
        <a-form-model :label-col="{ span: 6 }" :wrapper-col="{ span: 16 }">
          <a-form-model-item label="角色名" required>
            <a-input v-model="form.name" placeholder="请输入角色名" allow-clear />
          </a-form-model-item>
          <a-form-model-item label="备注">
            <a-textarea v-model="form.remark" :rows="3" placeholder="选填" allow-clear />
          </a-form-model-item>
        </a-form-model>

        <div class="perm-section-title">权限配置</div>
        <div v-for="group in permissionUiGroups" :key="group.title" class="perm-group">
          <div class="perm-group-title">{{ group.title }}</div>
          <a-checkbox
            v-for="item in group.items"
            :key="item.key"
            :checked="hasPermBits(permBits, item.def)"
            class="perm-checkbox"
            @change="(e) => onPermChange(item.def, e.target.checked)"
          >
            {{ item.label }}
          </a-checkbox>
        </div>

        <div class="drawer-footer">
          <a-button style="margin-right: 8px" @click="closeDrawer">取消</a-button>
          <a-button type="primary" :loading="saving" @click="saveRole">保存</a-button>
        </div>
      </a-spin>
    </a-drawer>
  </div>
</template>

<script>
import CommonPermissions from '@/components/CommonPermissions'
import RoleApi from '@/api/src/role/api/RoleApi'
import permissionDef from '@/utils/permission_def.js'
import { normalizePermissions, hasPermBits, setPermBits } from '@/utils/permissionBits.js'

const roleApi = new RoleApi()

function buildPermissionUiGroups() {
  return permissionDef.map((resource) => ({
    title: resource.label,
    items: (resource.operations || []).map((op) => ({
      key: `${resource.name}_${op.name}`,
      label: op.label,
      def: [resource.value, op.value],
      tagLabel: `${resource.label}·${op.label}`,
    })),
  }))
}

const flatPermissionItems = buildPermissionUiGroups().reduce((acc, g) => acc.concat(g.items), [])

export default {
  name: 'RoleList',
  components: { CommonPermissions },
  data() {
    return {
      queryName: '',
      loading: false,
      roleRows: [],
      currentPage: 1,
      pageSize: 10,
      total: 0,
      columns: [
        { title: '角色名', dataIndex: 'name', key: 'name' },
        { title: '备注', dataIndex: 'remark', key: 'remark', ellipsis: true },
        { title: '权限', key: 'permissions', scopedSlots: { customRender: 'permissions' } },
        { title: '操作', key: 'action', scopedSlots: { customRender: 'action' }, width: 160 },
      ],
      drawerVisible: false,
      drawerLoading: false,
      saving: false,
      editingId: undefined,
      form: {
        name: '',
        remark: '',
      },
      permBits: normalizePermissions([]),
      permissionUiGroups: buildPermissionUiGroups(),
    }
  },
  computed: {
    drawerTitle() {
      return this.editingId ? '编辑角色' : '新建角色'
    },
    pagination() {
      return {
        current: this.currentPage,
        pageSize: this.pageSize,
        total: this.total,
        showSizeChanger: true,
        showTotal: (t) => `共 ${t} 条`,
        pageSizeOptions: ['10', '20', '50'],
      }
    },
  },
  mounted() {
    this.fetchList()
  },
  methods: {
    hasPermBits,
    fetchList() {
      this.loading = true
      const opts = {}
      if (this.queryName && String(this.queryName).trim()) {
        opts.name = String(this.queryName).trim()
      }
      roleApi
        .getPagingRole(this.currentPage, opts)
        .then((res) => {
          this.roleRows = (res && res.rows) || []
          this.total = (res && res.total) || 0
        })
        .catch(() => {
          this.$message.error('加载角色列表失败')
        })
        .finally(() => {
          this.loading = false
        })
    },
    handleSearch() {
      this.currentPage = 1
      this.fetchList()
    },
    handleTableChange(pag) {
      this.currentPage = pag.current
      this.pageSize = pag.pageSize
      this.fetchList()
    },
    permissionTagList(record) {
      const bits = normalizePermissions(record.permissions)
      const labels = []
      for (let i = 0; i < flatPermissionItems.length; i++) {
        const item = flatPermissionItems[i]
        if (hasPermBits(bits, item.def)) {
          labels.push(item.tagLabel)
        }
      }
      return labels
    },
    onPermChange(def, checked) {
      this.permBits = setPermBits(this.permBits, def, checked)
    },
    emptyForm() {
      this.editingId = undefined
      this.form = { name: '', remark: '' }
      this.permBits = normalizePermissions([])
    },
    closeDrawer() {
      this.drawerVisible = false
    },
    openDrawer(id) {
      if (id) {
        this.editingId = id
        this.drawerVisible = true
        this.drawerLoading = true
        roleApi
          .getRoleById(id)
          .then((res) => {
            this.form = {
              name: res.name || '',
              remark: res.remark || '',
            }
            this.permBits = normalizePermissions(res.permissions)
          })
          .catch(() => {
            this.$message.error('加载角色失败')
            this.drawerVisible = false
          })
          .finally(() => {
            this.drawerLoading = false
          })
      } else {
        this.emptyForm()
        this.drawerVisible = true
      }
    },
    saveRole() {
      const name = (this.form.name || '').trim()
      if (!name) {
        this.$message.warning('请填写角色名')
        return
      }
      if (this.saving) {
        return
      }
      this.saving = true
      const body = {
        name,
        remark: this.form.remark || '',
        permissions: normalizePermissions(this.permBits),
      }
      const done = () => {
        this.saving = false
        this.closeDrawer()
        this.fetchList()
      }
      const fail = () => {
        this.saving = false
      }
      if (this.editingId) {
        roleApi
          .updateRole(this.editingId, body)
          .then(() => {
            this.$message.success('保存成功')
            done()
          })
          .catch(() => {
            this.$message.error('保存失败')
            fail()
          })
      } else {
        roleApi
          .addRole(body)
          .then(() => {
            this.$message.success('创建成功')
            done()
          })
          .catch(() => {
            this.$message.error('创建失败')
            fail()
          })
      }
    },
    removeRole(id) {
      this.loading = true
      roleApi
        .deleteRoleById(id)
        .then(() => {
          this.$message.success('已删除')
          this.fetchList()
        })
        .catch(() => {
          this.$message.error('删除失败')
          this.loading = false
        })
    },
  },
}
</script>

<style scoped>
.role-list-page {
  padding: 16px;
}
.toolbar {
  margin-bottom: 16px;
}
.perm-empty {
  color: rgba(0, 0, 0, 0.25);
}
.perm-more {
  color: rgba(0, 0, 0, 0.45);
  font-size: 12px;
  margin-left: 4px;
}
.perm-section-title {
  font-weight: 600;
  margin-bottom: 12px;
  margin-top: 8px;
}
.perm-group {
  margin-bottom: 16px;
}
.perm-group-title {
  color: rgba(0, 0, 0, 0.65);
  margin-bottom: 8px;
  font-size: 13px;
}
.perm-checkbox {
  margin-right: 16px;
  margin-bottom: 8px;
}
.drawer-footer {
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
}
</style>
